import type { ReactNode } from 'react'

import { QuoteCarrier } from '@fv/client-components'
import type { FullShipment } from '@fv/client-types'
import {
  type ComparableLoadsLeadTimeFilter,
  comparableLoadsLeadTimeFilters,
  getComparableLoadsLeadTimeFilterLabel,
} from '@fv/models'
import {
  type ComparableLoadsLaneToleranceRadius,
  type ComparableLoadsWindow,
  comparableLoadsLaneToleranceRadii,
  comparableLoadsWindows,
  getComparableLoadsWindowLabel,
} from '@fv/models/admin'
import { formatMoney } from '@fv/models/core'

import { InputGroup } from '@/components/inputs/InputGroup'
import { Loading } from '@/components/Loading'
import { PageHeader } from '@/components/PageHeader'
import { ShipperSlider } from '@/components/ShipperSlider'

import { useComparableLoadPrefs, useComparableLoads } from './api'
import { getLoadCheapestCostPerMile } from './utils'

const formatOptionForPendingLoad = (label: string) =>
  label.replace('±', 'Last ')

const bookedWindowOptions = comparableLoadsWindows.map(value => ({
  value,
  text: formatOptionForPendingLoad(getComparableLoadsWindowLabel(value)),
}))

const laneToleranceOptions = comparableLoadsLaneToleranceRadii.map(value => ({
  value: String(value),
  text: `${value} miles`,
}))

const leadTimeFilterOptions = comparableLoadsLeadTimeFilters.map(value => ({
  value,
  text: getComparableLoadsLeadTimeFilterLabel(value),
}))

const formatRate = (amount: number | null | undefined) =>
  amount != null ? formatMoney(amount, 'usd') : '—'

const formatCostPerMile = (amount: number | null | undefined) => {
  if (!amount) return '—'
  return `${formatMoney(amount, 'usd')}/mi`
}
type Props = {
  load: FullShipment
  onClose: () => void
  isOpen: boolean
}

export const ComparableLoadsPanel = ({ load, onClose, isOpen }: Props) => {
  const query = useComparableLoads(load.loadId)
  const [prefs, setComparablePrefs] = useComparableLoadPrefs()
  if (!isOpen) return null

  const { isFetching, data } = query
  const thisLoad = getLoadCheapestCostPerMile(load)

  return (
    <ShipperSlider onClose={onClose} isOpen={isOpen}>
      <div className="flex flex-col gap-4">
        <PageHeader
          icon="ballot-check"
          title="Shipment historical pricing"
        ></PageHeader>
        <div className="border-b border-fv-gray">
          <div className="grid grid-cols-3 gap-x-4">
            <div>
              <InputGroup
                label="Booked date window"
                helpText="Only include shipments booked within this range"
                inputType="select"
                inputProps={{
                  name: 'bookedWindow',
                  options: bookedWindowOptions,
                  value: prefs.bookedWindow,
                  onChange: event =>
                    setComparablePrefs(prev => ({
                      ...prev,
                      bookedWindow: event.target.value as ComparableLoadsWindow,
                    })),
                }}
              />
            </div>
            <div>
              <InputGroup
                label="Lane tolerance"
                helpText="How far pickup and delivery can be from this shipment's lane"
                inputType="select"
                inputProps={{
                  name: 'laneToleranceRadiusMiles',
                  options: laneToleranceOptions,
                  value: String(prefs.laneToleranceRadiusMiles),
                  onChange: event =>
                    setComparablePrefs(prev => ({
                      ...prev,
                      laneToleranceRadiusMiles: Number(
                        event.target.value,
                      ) as ComparableLoadsLaneToleranceRadius,
                    })),
                }}
              />
            </div>
            <div>
              <InputGroup
                label="Lead time"
                helpText="Filter by difference between pickup date and quote request date"
                inputType="select"
                inputProps={{
                  name: 'leadTimeFilter',
                  options: leadTimeFilterOptions,
                  value: prefs.leadTimeFilter,
                  onChange: event =>
                    setComparablePrefs(prev => ({
                      ...prev,
                      leadTimeFilter: event.target
                        .value as ComparableLoadsLeadTimeFilter,
                    })),
                }}
              />
            </div>
          </div>
        </div>
        {isFetching ? (
          <Loading />
        ) : (
          data && (
            <div className="grid grid-cols-3 divide-x *:px-4">
              <StatSection
                label="Lane average"
                value={formatCostPerMile(data.account.costPerMileAverage)}
                detail={
                  data.account.count > 0 && (
                    <>
                      {data.account.count} matching{' '}
                      {data.account.average != null && (
                        <> · avg {formatRate(data.account.average)}</>
                      )}
                    </>
                  )
                }
              />
              <StatSection
                label="This load"
                value={
                  thisLoad.costPerMile != null
                    ? formatCostPerMile(thisLoad.costPerMile)
                    : '—'
                }
                detail={
                  thisLoad.costPerMile != null && (
                    <>
                      Cheapest rate
                      {thisLoad.amount != null && (
                        <> · {formatRate(thisLoad.amount)}</>
                      )}
                      {thisLoad.distance != null && (
                        <> · {Math.round(thisLoad.distance)} mi</>
                      )}
                    </>
                  )
                }
              />
              <StatSection
                label="Most recent"
                value={
                  data.mostRecent
                    ? formatCostPerMile(data.mostRecent.costPerMile)
                    : '—'
                }
                detail={
                  data.mostRecent && (
                    <>
                      {formatRate(data.mostRecent.amount)} from{' '}
                      <QuoteCarrier
                        quote={{ providerName: data.mostRecent.carrierName }}
                      />
                    </>
                  )
                }
              />
            </div>
          )
        )}
      </div>
    </ShipperSlider>
  )
}

type StatSectionProps = {
  label: ReactNode
  value: ReactNode
  detail: ReactNode
}
const StatSection = ({ label, value, detail }: StatSectionProps) => {
  return (
    <div className="text-sm text-center">
      <div className="text-fv-gray-dark">{label}</div>
      <div className="text-lg font-semibold">{value}</div>
      <div className=" text-fv-gray-dark">{detail}</div>
    </div>
  )
}
